"use client";

import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { CTABanner } from "@/components/shared/CTABanner";
import { EASE } from "@/lib/motion";
import { videos, type VideoItem } from "@/lib/videos";

function VideoCard({ video, index }: { video: VideoItem; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.07, ease: EASE }}
      className="flex flex-col gap-4"
    >
      <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint">
        <span className="font-display font-semibold text-accent tnum normal-case text-base leading-none">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="w-4 h-px bg-line" aria-hidden="true" />
        Erklärvideo
      </p>

      <div className="rounded-2xl overflow-hidden border border-line bg-ink">
        <div
          className="relative w-full mx-auto"
          style={{
            paddingTop: video.orientation === "portrait" ? "177.78%" : "56.25%",
            maxWidth: video.orientation === "portrait" ? "420px" : "100%",
          }}
        >
          <iframe
            src={video.src}
            title={video.label}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function ErklaervideosContent() {
  return (
    <>
      <PageHeader
        eyebrow="Erklärvideos"
        title={
          <>
            So einfach funktioniert <span className="text-accent">ncrechner</span>
          </>
        }
        subtitle="Schritt-für-Schritt-Videos zur App, entstanden in Zusammenarbeit mit Celina von einfach.mfa."
      />

      {/* ── Intro copy ── */}
      <section className="bg-paper pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="space-y-4 text-ink-soft leading-relaxed"
          >
            <p>
              In unseren Erklärvideos zeigen wir dir Schritt für Schritt, wie du
              die ncrechner App optimal nutzt. Du erfährst, wie du deine
              Abiturnote und deinen Prozentrang korrekt einträgst, wie du
              Studierfähigkeitstests wie den TMS, HamNat oder PhaST
              berücksichtigst und wie du Dienste, Ausbildungen und Wartesemester
              in die Berechnung einfließen lässt. So verstehst du nicht nur,
              welche Zulassungschancen du an den einzelnen Universitäten hast,
              sondern auch, wie diese Werte zustande kommen.
            </p>
            <p>
              Die Videos richten sich an alle, die sich für ein Studium der
              Humanmedizin, Zahnmedizin, Tiermedizin oder Pharmazie bewerben
              möchten – egal, ob du dich zum ersten Mal mit dem Auswahlverfahren
              beschäftigst oder deine Bewerbung gezielt optimieren willst. Nimm
              dir am besten die App parallel zur Hand und rechne direkt mit,
              während du die einzelnen Schritte durchgehst.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Videos ── */}
      <section className="bg-paper py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            badge="Alle Videos"
            title="Jedes Feature erklärt"
            subtitle="Unsere Erklärvideos zeigen dir Schritt für Schritt, wie du die App optimal nutzt: von der ersten Note bis zur finalen Auswertung."
          />

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {videos.map((video, i) => (
              <VideoCard key={video.src} video={video} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Partner + Ressourcen ── */}
      <section className="bg-paper-deep py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-ink-faint mb-3">
              In Zusammenarbeit mit
            </p>
            <h3 className="font-display font-bold text-2xl text-ink">Celina von einfach.mfa</h3>
            <p className="text-ink-soft leading-relaxed mt-3 max-w-2xl">
              Die Videos entstanden gemeinsam mit Celina, die auf ihrem Kanal
              einfach.mfa praxisnahe Inhalte rund um medizinische Ausbildung und
              Studium teilt.
            </p>
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
            href="https://www.dein-medizinstudium.de"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-5 mt-12 py-6 border-y border-line cursor-pointer"
          >
            <span className="flex-1">
              <span className="block text-xs font-bold uppercase tracking-widest text-ink-faint mb-1">
                Weitere Ressource
              </span>
              <span className="block font-display font-bold text-lg text-ink group-hover:text-accent transition-colors duration-200">
                dein-medizinstudium.de
              </span>
              <span className="block text-ink-soft text-sm leading-relaxed mt-1">
                Detaillierte Infos zu Zulassungsvoraussetzungen und Hochschulvergleich für alle deutschen Universitäten.
              </span>
            </span>
            <ArrowUpRight
              size={18}
              className="text-ink-faint group-hover:text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200 shrink-0"
              aria-hidden="true"
            />
          </motion.a>
        </div>
      </section>

      <CTABanner
        title="Jetzt selbst ausprobieren."
        text="Lade ncrechner kostenlos herunter und berechne in wenigen Minuten deine Zulassungschancen für alle deutschen Hochschulen."
      />
    </>
  );
}
